import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { projectsAPI, filesAPI } from '../api';
import SEO from '../components/SEO';
import ProjectFeed from '../components/ProjectFeed';
import ProjectBackers from '../components/ProjectBackers';
import ProjectBackingSidebar from '../components/ProjectBackingSidebar';
import MarkdownContent from '../components/MarkdownContent';

const extractYoutubeId = (url) => {
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  return (match && match[2].length === 11) ? match[2] : null;
};

function PublicProjectPage() {
  const { slug } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lightboxImage, setLightboxImage] = useState(null);
  const [userBacking, setUserBacking] = useState(null);
  const [openBuyTrigger, setOpenBuyTrigger] = useState(0);

  useEffect(() => {
    loadProject();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  useEffect(() => {
    if (project && project.pricing_type !== 'free') {
      loadBackingStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project]);

  const loadProject = async () => {
    setLoading(true);
    try {
      const response = await projectsAPI.getBySlug(slug);
      if (response.data.success) {
        setProject(response.data.project);
      }
    } catch (error) {
      console.error('Error loading project:', error);
      setError('Project not found');
    } finally {
      setLoading(false);
    }
  };

  const loadBackingStatus = async () => {
    if (!project) return;
    try {
      const response = await projectsAPI.getBackers(project.project_id);
      if (response.data.success) {
        setUserBacking(response.data.user_backing || null);
      }
    } catch (error) {
      console.log('Could not load backing status:', error);
    }
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const formatNumber = (num) => {
    if (num >= 1000) return (num / 1000).toFixed(1) + 'k';
    return num;
  };

  const handleDownload = (fileId) => {
    // Use project download endpoint to track downloads properly
    const downloadUrl = projectsAPI.downloadProjectFile(project.project_id, fileId);
    window.open(downloadUrl, '_blank');
  };

  const getIconUrl = (iconPath) => {
    if (!iconPath) return null;
    if (iconPath.startsWith('/api/')) {
      const API_URL = process.env.REACT_APP_API_URL || '';
      // Remove '/api' prefix (4 chars) since API_URL already ends with /api
      return API_URL + iconPath.substring(4);
    }
    return iconPath;
  };

  if (loading) {
    return (
      <div className="public-project-page">
        <div className="container">
          <div className="loading">
            <div className="spinner"></div>
            <p>Loading project...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !project) {
    return (
      <>
        <SEO 
          title="Project Not Found"
          description="The project you're looking for doesn't exist or is no longer available."
        />
        <div className="public-project-page">
          <div className="container">
            <div className="error-state">
              <h2>Project Not Found</h2>
              <p className="text-muted">
                The project you're looking for doesn't exist or is no longer available.
              </p>
              <Link to="/browse" className="btn btn-primary mt-3">
                Browse Projects
              </Link>
            </div>
          </div>
        </div>
      </>
    );
  }

  const projectJsonLd = {
    "@context": "https://schema.org",
    "@type": "SoftwareSourceCode",
    "name": project.title,
    "description": project.description || project.short_description || "Download files for " + project.title,
    "url": `https://sharepanda.org/p/${project.slug}`,
    "author": {
      "@type": "Person",
      "name": project.owner
    },
    "dateCreated": project.created_at,
    "dateModified": project.updated_at,
    "programmingLanguage": "Multi-platform"
  };

  return (
    <>
      <SEO 
        title={project.title}
        description={project.description || project.short_description || `Download ${project.title} files. ${project.downloads.length} files available for download.`}
        keywords={`${project.title}, download, file sharing, APK, IPA, ${project.owner}`}
        url={`https://sharepanda.org/p/${project.slug}`}
        type="article"
        jsonLd={projectJsonLd}
      />
      <div className="public-project-page">
        <div className="container">
          {/* Header */}
        <div className="public-project-header">
          <Link to="/browse" className="back-link">
            ← Browse Projects
          </Link>
          
          <div className="project-title-section">
            {project.thumbnail ? (
              <img 
                src={getIconUrl(project.thumbnail)} 
                alt={project.title} 
                className="project-icon-large"
              />
            ) : (
              <div className="project-icon-large">
                {project.title.charAt(0).toUpperCase()}
              </div>
            )}
            <div>
              <h1 className="public-project-title">{project.title}</h1>
              {project.short_description && (
                <p className="public-project-tagline">{project.short_description}</p>
              )}
            </div>
          </div>
        </div>

        <div className="public-content-grid">
          {/* Main Content */}
          <div className="public-main-content">
            {/* Description */}
            {project.description && (
              <div className="card public-description-card">
                <h2 className="section-title-small">About</h2>
                <div className="public-description">
                  <MarkdownContent content={project.description} />
                </div>
              </div>
            )}

            {/* Downloads */}
            <div className="card">
              <h2 className="section-title-small">Downloads</h2>
              
              {project.downloads.length === 0 ? (
                <div className="empty-downloads">
                  <p className="text-muted">No files available yet</p>
                </div>
              ) : (
                <div className="downloads-list">
                  {project.downloads.map((file) => (
                    <div key={file.file_id} className="download-item">
                      <div className="download-item-info">
                        <h3 className="download-item-name">{file.filename}</h3>
                        <div className="download-item-meta">
                          {file.version && (
                            <span className="download-meta-item">
                              <span className="badge badge-info">{file.version}</span>
                            </span>
                          )}
                          {file.platform && (
                            <span className="download-meta-item">
                              {file.platform}
                            </span>
                          )}
                          <span className="download-meta-item">
                            {formatBytes(file.size)}
                          </span>
                          <span className="download-meta-item">
                            {formatNumber(file.downloads || 0)} downloads
                          </span>
                        </div>
                      </div>
                      {project.pricing_type !== 'free' && !file.is_free && userBacking?.payment_status !== 'paid' ? (
                        <button
                          className="btn btn-secondary download-button"
                          disabled={userBacking?.payment_status === 'pending'}
                          title={userBacking?.payment_status === 'pending' ? 'Payment pending verification' : 'Purchase required to download'}
                          onClick={() => {
                            if (userBacking?.payment_status !== 'pending') {
                              setOpenBuyTrigger(Date.now());
                            }
                          }}
                        >
                          {userBacking?.payment_status === 'pending' ? '⏳ Pending' : '🔒 Buy to Download'}
                        </button>
                      ) : (
                        <button
                          onClick={() => handleDownload(file.file_id)}
                          className="btn btn-primary download-button"
                        >
                          <span>⬇</span> {file.is_free && project.pricing_type !== 'free' ? 'Free Download' : 'Download'}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Screenshots */}
            {project.screenshots && project.screenshots.length > 0 && (
              <div className="card">
                <h2 className="section-title-small">Screenshots</h2>
                <div className="screenshots-gallery">
                  {project.screenshots.map((screenshot) => {
                    // Handle relative URLs for uploaded screenshots
                    const API_URL = process.env.REACT_APP_API_URL || '';
                    let imgSrc = screenshot.url;
                    if (!imgSrc && screenshot.id) {
                      // Construct URL from screenshot ID if url is missing
                      imgSrc = `/api/projects/${project.project_id}/screenshots/file/${screenshot.id}`;
                    }
                    if (imgSrc && imgSrc.startsWith('/api/')) {
                      imgSrc = API_URL + imgSrc.substring(4);
                    }
                    return (
                    <div key={screenshot.id} className="screenshot-item" onClick={() => setLightboxImage({ src: imgSrc, caption: screenshot.caption })} style={{ cursor: 'pointer' }}>
                      <img 
                        src={imgSrc} 
                        alt={screenshot.caption || 'Screenshot'} 
                        className="screenshot-image"
                      />
                      {screenshot.caption && (
                        <p className="screenshot-caption">{screenshot.caption}</p>
                      )}
                    </div>
                  );})}
                </div>
              </div>
            )}

            {/* Videos */}
            {project.videos && project.videos.length > 0 && (
              <div className="card">
                <h2 className="section-title-small">Videos</h2>
                <div className="videos-grid">
                  {project.videos.map((video) => (
                    <div key={video.id} className="video-item">
                      {video.type === 'youtube' ? (
                        <>
                          <div className="video-embed-container">
                            <iframe
                              width="100%"
                              height="315"
                              src={`https://www.youtube.com/embed/${extractYoutubeId(video.url)}`}
                              title={video.title || 'Video'}
                              frameBorder="0"
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                              allowFullScreen
                            ></iframe>
                          </div>
                          {video.title && (
                            <p className="video-title">{video.title}</p>
                          )}
                        </>
                      ) : (
                        <>
                          <div className="video-embed-container">
                            <video width="100%" height="315" controls>
                              <source src={video.url} type="video/mp4" />
                              Your browser does not support the video tag.
                            </video>
                          </div>
                          {video.title && (
                            <p className="video-title">{video.title}</p>
                          )}
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Latest Updates - Project Feed */}
            <div className="card">
              <h2 className="section-title-small">Latest Updates</h2>
              <ProjectFeed 
                projectId={project.project_id} 
                isOwner={false} 
                username={null}
              />
            </div>

            {/* Project Backers */}
            <ProjectBackers projectId={project.project_id} />
          </div>

          {/* Sidebar */}
          <div className="public-sidebar">
            {/* Backing Card */}
            <ProjectBackingSidebar
              projectId={project.project_id}
              project={project}
              onPaymentConfirmed={loadBackingStatus}
              openBuyTrigger={openBuyTrigger}
            />
            
            {/* Quick Info */}
            <div className="card sidebar-card">
              <h3 className="sidebar-title">Project Info</h3>
              <div className="sidebar-info-list">
                <div className="sidebar-info-item">
                  <span className="sidebar-info-label">Created</span>
                  <span className="sidebar-info-value">
                    {new Date(project.created_at).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </span>
                </div>
                <div className="sidebar-info-item">
                  <span className="sidebar-info-label">Last Updated</span>
                  <span className="sidebar-info-value">
                    {new Date(project.updated_at).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </span>
                </div>
                {project.project_url && (
                  <div className="sidebar-info-item">
                    <span className="sidebar-info-label">Website</span>
                    <a
                      href={project.project_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="sidebar-link"
                    >
                      Visit →
                    </a>
                  </div>
                )}
                {(project.privacy_policy || project.terms_of_use) && (
                  <div className="sidebar-info-item">
                    <span className="sidebar-info-label">Legal</span>
                    <span className="sidebar-info-value">
                      {project.privacy_policy && (
                        <Link
                          to={`/p/${project.slug}/privacy`}
                          className="sidebar-link"
                        >
                          Privacy
                        </Link>
                      )}
                      {project.privacy_policy && project.terms_of_use && (
                        <span className="text-muted"> · </span>
                      )}
                      {project.terms_of_use && (
                        <Link
                          to={`/p/${project.slug}/terms`}
                          className="sidebar-link"
                        >
                          Terms
                        </Link>
                      )}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Stats */}
            <div className="card sidebar-card">
              <h3 className="sidebar-title">Statistics</h3>
              <div className="sidebar-stats-grid">
                <div className="sidebar-stat-item">
                  <div className="sidebar-stat-value">{formatNumber(project.stats.total_downloads)}</div>
                  <div className="sidebar-stat-label">Downloads</div>
                </div>
                <div className="sidebar-stat-item">
                  <div className="sidebar-stat-value">{formatNumber(project.stats.views)}</div>
                  <div className="sidebar-stat-label">Views</div>
                </div>
                <div className="sidebar-stat-item">
                  <div className="sidebar-stat-value">{project.downloads.length}</div>
                  <div className="sidebar-stat-label">Files</div>
                </div>
              </div>
            </div>

            {/* Share */}
            <div className="card sidebar-card">
              <h3 className="sidebar-title">Share</h3>
              <p className="text-small text-muted mb-2">
                Share this project with others
              </p>
              <input
                type="text"
                className="form-control"
                value={window.location.href}
                readOnly
                onClick={(e) => e.target.select()}
              />
              <button
                onClick={() => {
                  navigator.clipboard.writeText(window.location.href);
                  alert('Link copied to clipboard!');
                }}
                className="btn btn-secondary mt-2"
                style={{ width: '100%' }}
              >
                📋 Copy Link
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Lightbox Modal */}
      {lightboxImage && (
        <div className="lightbox-overlay" onClick={() => setLightboxImage(null)}>
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <button className="lightbox-close" onClick={() => setLightboxImage(null)}>×</button>
            <img src={lightboxImage.src} alt={lightboxImage.caption || 'Screenshot'} className="lightbox-image" />
            {lightboxImage.caption && <p className="lightbox-caption">{lightboxImage.caption}</p>}
          </div>
        </div>
      )}
    </div>
    </>
  );
}

export default PublicProjectPage;
